import axios from "axios";
import cogoToast from "cogo-toast";
import config from "../../utils/config";
import i18n from "../../i18n";
// import { persistor } from '../store';

const { createSlice, createAsyncThunk, isAnyOf } = require("@reduxjs/toolkit");

// Access auth token
export const fetchAccessToken = createAsyncThunk(
  "auth/fetchAccessToken",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/auth/token", payload);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// login API
export const login = createAsyncThunk(
  "auth/login",
  async (
    { userLoginId, password, tenantId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/services/login", {
        userLoginId,
        password,
        tenantId,
        isB2B: true,
        host: window.location.hostname
      });

      if (response.status === 200) {
        if (response?.data?.data?.message) {
          cogoToast.error(response.data.data.message, {
            position: "top-right",
            hideAfter: 5,
          });
        } else {
          cogoToast.success("Logged in successfully!", {
            position: "top-right",
            hideAfter: 5,
          });
        }
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// logout API
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const userDetails = auth?.userDetails || {};
      const response = await axios.post("/services/logout", {
        userLoginId: userDetails?.userLoginId,
      });

      if (response.status === 200) {
        cogoToast.success(response.data.successMessage, {
          position: "top-right",
          hideAfter: 5,
        });
      }
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// get all clients
export const getClientList = createAsyncThunk(
  "auth/getClientList",
  async ({ setClients }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/getClientList", {
        webSiteId: config.websiteId,
      });

      if (response.status === 200) {
        setClients(response?.data?.data?.clientList || []);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Retrieve shipping addresses by party
export const retrieveShippingAddress = createAsyncThunk(
  "product/retrieveShippingAddress",
  async ({ partyId, setShippingAddresses }, { rejectWithValue }) => {
    try {
      if (!partyId) {
        return rejectWithValue("partyId mancante");
      }

      // POST con body JSON, non in querystring
      const response = await axios.post("/services/retrieveShippingAddress", { partyId });

      if (response.status === 200) {
        const payload = response?.data?.data ?? response?.data ?? {};
        const addresses = payload?.shippingAddress ?? [];
        setShippingAddresses(addresses);
      }

      return response?.data?.data ?? response?.data;
    } catch (error) {
      if (error.response?.data) {
        return rejectWithValue(
          error.response.data.errorMessage || error.response.data.message
        );
      }
      return rejectWithValue(error.message);
    }
  }
);

// Get downloadable files
export const getFiles = createAsyncThunk(
  "auth/getFiles",
  async ({ setFiles }, { rejectWithValue }) => {
    try {
      // partyId is unused server-side for now (always the logged-in user's own
      // agreements), but the REST GET layer still requires the `inParams` query
      // param to be present regardless of whether the service needs any input.
      const response = await axios.get(
        `/services/getFiles?inParams=${encodeURIComponent(JSON.stringify({}))}`
      );

      if (response.status === 200) {
        // Map of folder name (product category id) -> array of file entries.
        const filesByFolder = response?.data?.data?.responseMap?.files || {};
        setFiles(filesByFolder);
      }

      return response?.data?.data;
    } catch (error) {
      cogoToast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Errore nel recupero dei dati}",
        {
          position: "top-right",
          hideAfter: 5,
        }
      );

      if (error.response?.data) {
        return rejectWithValue(
          error.response.data.errorMessage || error.response.data.message
        );
      }
      return rejectWithValue(error.message);
    }
  }
);

// Get order Details
export const getOrderDetails = createAsyncThunk(
  "product/getOrderDetails",
  async ({ orderId, setOrderDetails }, thunkAPI) => {
    try {
      const encodedURI = `/services/getOrderDetail?inParams=${encodeURIComponent(
        JSON.stringify({ orderId, isUpdate: "Y" })
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setOrderDetails(response?.data?.data?.orderDetails);
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Get order PDF (base64-encoded) and open it in a new tab
export const getOrderPdf = createAsyncThunk(
  "auth/getOrderPdf",
  async ({ orderId }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/getOrderPdf", {
        orderId,
        localeCode: i18n.language,
      });

      const pdfBase64 = response?.data?.data?.pdfBase64;
      if (response.status === 200 && pdfBase64) {
        const byteCharacters = atob(pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], {
          type: "application/pdf",
        });
        window.open(URL.createObjectURL(blob), "_blank");
      }
      return response?.data?.data;
    } catch (error) {
      cogoToast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Errore nella generazione del PDF",
        {
          position: "top-right",
          hideAfter: 5,
        }
      );
      if (error.response?.data) {
        return rejectWithValue(
          error.response.data.errorMessage || error.response.data.message
        );
      }
      return rejectWithValue(error.message);
    }
  }
);

// Get Agreement Terms API
export const getAgreementTerms = createAsyncThunk(
  "party/getAgreementTerms",
  async (
    {
      setAgreementTerms,
      agreementId,
      orderId = "",
    },
    thunkAPI
  ) => {
    try {
      const inParams = {
        agreementId,
        orderId,
      };
      const encodedURI = `/services/getAgreementTerms?inParams=${encodeURIComponent(
        JSON.stringify(inParams)
      )}`;
      const response = await axios.get(encodedURI);

      if (response.status === 200) {
        setAgreementTerms(response?.data?.data?.agreementTerms || []);
        return response.data;
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response?.data);
    }
  }
);

//Get Agreement Items API
export const getAgreementItems = createAsyncThunk(
  "party/getAgreementItems",
  async (
    {
      setAgreementItems,
      agreementId,
      orderId = "",
    },
    thunkAPI
  ) => {
    try {
      const inParams = {
        agreementId,
        orderId,
      };
      const encodedURI = `/services/getAgreementItems?inParams=${encodeURIComponent(
        JSON.stringify(inParams)
      )}`;
      const response = await axios.get(encodedURI);

      if (response.status === 200) {
        setAgreementItems(response?.data?.data?.agreementItems || []);
        return response.data;
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response?.data);
    }
  }
);

// Get Product Agreement Categories
export const getAgreementProductCategories = createAsyncThunk(
  "party/getAgreementProductCategories",
  async (
    {
      setAgreementProductCategory,
      agreementId,
      agreementItemSeqId,
      orderId = "",
    },
    thunkAPI
  ) => {
    try {
      const inParams = {
        agreementId,
        agreementItemSeqId,
        orderId,
      };
      const encodedURI = `/services/getAgreementProductCategories?inParams=${encodeURIComponent(
        JSON.stringify(inParams)
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setAgreementProductCategory(
          response?.data?.data?.agreementProductCategories ?? []
        );
        return response.data;
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response?.data);
    }
  }
);

// Get AgentInfo
export const getAgentInfo = createAsyncThunk(
  "product/getAgentInfo",
  async ({ setAgentInfo }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const agentPartyId = auth?.client?.partyId || "";
      const encodedURI = `/services/getAgentInfo?inParams=${encodeURIComponent(
        JSON.stringify({ agentPartyId })
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setAgentInfo(response?.data?.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Get Agent Orders
export const getAgentOrders = createAsyncThunk(
  "product/getAgentOrders",
  async (
    {
      viewSize = 10,
      viewIndex = 0,
      sortField = "",
      appliedFilters,
      setAgentOrders,
      setOrganizations,
      setTotalRecords,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState();
      const agentPartyId = auth?.client?.partyId || "";
      const params = {
        viewSize,
        viewIndex,
        sortField,
        ...appliedFilters,
      };
      const encodedURI = `/services/getAgentOrders?inParams=${encodeURIComponent(
        JSON.stringify(params)
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setAgentOrders(response?.data?.data?.agentOrders);
        setOrganizations(response?.data?.data?.organizationNames);
        setTotalRecords(response?.data?.data?.totalRecords || 0);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Update order status
export const changeOrderStatus = createAsyncThunk(
  "product/changeOrderStatus",
  async ({ data }, thunkAPI) => {
    try {
      const response = await axios.post("/services/changeOrderStatus", data);
      if (response.status === 200) {
        cogoToast.success("Order status changed successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Update order shipping destination address
export const updateOrderShippingDestination = createAsyncThunk(
  "product/updateOrderShippingDestination",
  async ({ orderId, contactMechId, oldContactMechId }, thunkAPI) => {
    try {
      if (!orderId) {
        return thunkAPI.rejectWithValue("Missing orderId");
      }

      if (!contactMechId) {
        return thunkAPI.rejectWithValue("Missing contactMechId");
      }

      // Per SHIPPING_LOCATION in OFBiz è di fatto necessario:
      // senza oldContactMechId il servizio può aggiornare tutti gli ship group dell'ordine.
      if (!oldContactMechId) {
        return thunkAPI.rejectWithValue(
          "Missing oldContactMechId for shipping destination update"
        );
      }

      const response = await axios.post(
        "/services/updateOrderContactMech",
        {
          orderId,
          contactMechId,
          oldContactMechId,
          contactMechPurposeTypeId: "SHIPPING_LOCATION",
        }
      );

      const payload = response?.data?.data ?? response?.data ?? {};

      const isError =
        payload?.responseMessage === "error" ||
        Boolean(payload?.errorMessage) ||
        Boolean(payload?._ERROR_MESSAGE_);

      if (isError) {
        return thunkAPI.rejectWithValue(
          payload?.errorMessage ||
          payload?._ERROR_MESSAGE_ ||
          "Unable to update shipping destination"
        );
      }

      return {
        orderId,
        contactMechId,
        oldContactMechId,
        response: payload,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.errorMessage ||
        error?.response?.data?._ERROR_MESSAGE_ ||
        error?.message ||
        "Unable to update shipping destination"
      );
    }
  }
);

export const getPerformSearch = createAsyncThunk(
  "party/getPerformSearch",
  async (
    {
      setPerformSearch,
      entityName,
      inputFields = {},
      orderBy = "description",
      noConditionFind = "Y",
    },
    thunkAPI
  ) => {
    try {
      const params = {
        entityName,
        noConditionFind,
        inputFields,
        orderBy,
      };
      const encodedURI = `/services/peformSearch?inParams=${encodeURIComponent(
        JSON.stringify(params)
      )}`;

      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        const records = response?.data?.data?.records || [];
        setPerformSearch(records);
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(
          response.data || "Unexpected Error occurred"
        );
      }
    } catch (error) {
      // Error Handling
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Get return info
export const getReturnInfo = createAsyncThunk(
  "product/getReturnInfo",
  async ({ orderId, isSubstitution = false, setReturnInfo }, { rejectWithValue }) => {
    try {
      // isSubstitution: return values for a substitute order (same Y/N flag as createReturn)
      const encodedURI = `/services/getReturnInfo?inParams=${encodeURIComponent(
        JSON.stringify({ orderId, isSubstitution: isSubstitution ? "Y" : "N" })
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setReturnInfo(response?.data?.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Create return
export const createReturn = createAsyncThunk(
  "product/createReturn",
  async ({ data, callback }, thunkAPI) => {
    try {
      const response = await axios.post("/services/createReturn", data);
      if (response.status === 200) {
        cogoToast.success("Return created successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
        if (callback) callback(response?.data?.data?.returnId);
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Get return details
export const getReturnDetails = createAsyncThunk(
  "product/getReturnDetails",
  async ({ returnId, isSubstitution, setReturnDetails }, { rejectWithValue }) => {
    try {
      // isSubstitution (Y/N) only when the caller knows it; otherwise omitted (backend default)
      const inParams = { returnId };
      if (isSubstitution !== undefined) {
        inParams.isSubstitution = isSubstitution ? "Y" : "N";
      }
      const encodedURI = `/services/getReturnDetail?inParams=${encodeURIComponent(
        JSON.stringify(inParams)
      )}`;
      const response = await axios.get(encodedURI);
      if (response.status === 200) {
        setReturnDetails(response?.data?.data?.returnDetails || {});
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Initialize Substitute OrderCart
export const initializeSubstituteOrder = createAsyncThunk(
  "product/initializeSubstituteOrder",
  async ({ data, callback }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/services/initializeSubstituteOrderCart",
        data
      );

      const payload = response?.data?.data ?? {};

      // Like initializeOrderEditCart, a failure (e.g. return/order mismatch, expired
      // agreement) comes back as HTTP 200 with an `emessage` field and no cart.
      if (payload?.emessage) {
        cogoToast.error(payload.emessage, {
          position: "top-right",
          hideAfter: 5,
        });
        return rejectWithValue(payload.emessage);
      }

      if (response.status === 200 && callback) {
        callback(payload);
      }
      return payload;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Reopen a created order for editing (loads it into the working cart)
export const initializeOrderEditCart = createAsyncThunk(
  "auth/initializeOrderEditCart",
  async ({ orderId, callback }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/initializeOrderEditCart", {
        orderId,
      });

      const payload = response?.data?.data ?? {};

      // This service doesn't use ServiceUtil.returnError, so a failure still
      // comes back as HTTP 200 with an `emessage` field instead of a thrown error.
      if (payload?.emessage) {
        cogoToast.error(payload.emessage, {
          position: "top-right",
          hideAfter: 5,
        });
        return rejectWithValue(payload.emessage);
      }

      if (response.status === 200 && callback) {
        callback(payload);
      }

      return payload;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Update return header
export const updateReturnHeader = createAsyncThunk(
  "product/updateReturnHeader",
  async ({ data, callback }, thunkAPI) => {
    try {
      const response = await axios.post("/services/updateReturnHeader", data);
      if (response.status === 200) {
        cogoToast.success("Return status updated successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
        if (callback) callback();
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Add note
export const addNote = createAsyncThunk(
  "product/addNote",
  async ({ orderId, noteInfo, internalNote = "N", noteName }, thunkAPI) => {
    try {
      const response = await axios.post("/services/createOrderNote", {
        orderId,
        note: noteInfo,
        internalNote,
        noteName, // opzionale
      });

      if (response.status === 200) {
        cogoToast.success("Note added successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Get state list
export const getStates = createAsyncThunk(
  "product/getCountry",
  async (
    {
      geoId,
      setStates,
    },
    thunkAPI
  ) => {
    try {
      const endpoints = [
        `/services/getCountry`
      ];

      let data = null;
      for (const url of endpoints) {
        try {
          const res = await axios.get(url);
          if (res.status >= 200 && res.status < 300) {
            data = res.data;
            if (data) break;
          }
        } catch (e) {
          // try next endpoint
        }
      }

      if (!data) {
        return thunkAPI.rejectWithValue("No states data returned");
      }

      const list = Array.isArray(data)
        ? data
        : data.results || data.data?.countryList || data.countryList || [];

      setStates(list || []);
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const createNewCustomer = createAsyncThunk(
  "auth/createNewCustomer",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post("/services/createNewCustomer", payload);

      if (response.status === 200) {
        cogoToast.success("Client created successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
      }

      return response.data;
    } catch (error) {
      cogoToast.error(error?.response?.data?.message || "Error creating client", {
        position: "top-right",
        hideAfter: 5,
      });
      return thunkAPI.rejectWithValue(error?.response?.data || error.message);
    }
  }
);

const initialState = {
  accessToken: null,
  isLoading: false,
  client: null,
  shoppingCart: null,
  selectedCategories: [],
  tenantId: null,
  userDetails: null,
  cartProductCount: 0,
  orderCategoryId: null,
  selectedCatalogType: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setClient: (state, action) => {
      state.client = action.payload;
    },
    setShoppingCart: (state, action) => {
      state.shoppingCart = action.payload;
    },
    setSelectedCategories: (state, action) => {
      state.selectedCategories = action.payload;
    },
    setCartProductCount: (state, action) => {
      state.cartProductCount = action.payload;
    },
    setOrderCategoryId: (state, action) => {
      state.orderCategoryId = action.payload;
    },
    setSelectedCatalogType: (state, action) => {
      state.selectedCatalogType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(
          fetchAccessToken.pending,
          login.pending,
          getClientList.pending,
          getFiles.pending,
          retrieveShippingAddress.pending,
          getOrderDetails.pending,
          getOrderPdf.pending,
          changeOrderStatus.pending,
          updateOrderShippingDestination.pending,
          getAgentOrders.pending,
          getAgentInfo.pending,
          createReturn.pending,
          getReturnInfo.pending,
          getReturnDetails.pending,
          updateReturnHeader.pending,
          addNote.pending,
          createNewCustomer.pending,
          getStates.pending,
          initializeOrderEditCart.pending
        ),
        (state) => {
          state.isLoading = true;
        }
      )
      .addMatcher(isAnyOf(login.fulfilled), (state, action) => {
        state.isLoading = false;
        state.accessToken = action.payload.access_token;
        state.userDetails = action?.payload?.userDetails;
        if (action?.payload?.tenantId) {
          state.tenantId = action.payload.tenantId;
        }
      })
      .addMatcher(
        isAnyOf(
          login.rejected,
          getClientList.fulfilled,
          getClientList.rejected,
          getFiles.fulfilled,
          getFiles.rejected,
          retrieveShippingAddress.fulfilled,
          retrieveShippingAddress.rejected,
          getOrderDetails.fulfilled,
          getOrderDetails.rejected,
          getOrderPdf.fulfilled,
          getOrderPdf.rejected,
          changeOrderStatus.fulfilled,
          changeOrderStatus.rejected,
          updateOrderShippingDestination.fulfilled,
          updateOrderShippingDestination.rejected,
          getAgentOrders.fulfilled,
          getAgentOrders.rejected,
          getAgentInfo.fulfilled,
          getAgentInfo.rejected,
          createReturn.fulfilled,
          createReturn.rejected,
          getReturnInfo.fulfilled,
          getReturnInfo.rejected,
          getReturnDetails.fulfilled,
          getReturnDetails.rejected,
          updateReturnHeader.fulfilled,
          updateReturnHeader.rejected,
          addNote.fulfilled,
          addNote.rejected,
          createNewCustomer.fulfilled,
          createNewCustomer.rejected,
          getStates.fulfilled,
          getStates.rejected,
          initializeOrderEditCart.fulfilled,
          initializeOrderEditCart.rejected
        ),
        (state, action) => {
          state.isLoading = false;
        }
      )
      .addMatcher(isAnyOf(initializeOrderEditCart.fulfilled, initializeSubstituteOrder.fulfilled), (state, action) => {
        state.orderCategoryId = action.payload?.orderCategory || null;
      })
      .addMatcher(isAnyOf(logout.fulfilled), (state, action) => {
        state.accessToken = "";
        state.accessToken = "";
        state.userDetails = null;
        state.tenantId = null;
        state.client = null;
        state.shoppingCart = null;
        state.selectedCategories = [];
        state.cartProductCount = 0;
        state.orderCategoryId = null;
        state.selectedCatalogType = null;
        localStorage.clear("persist:B2B");
      });
  },
});

export const authSelector = (state) => state.auth;

export const { setClient, setShoppingCart, setSelectedCategories, setCartProductCount, setOrderCategoryId, setSelectedCatalogType } =
  authSlice.actions;
export default authSlice.reducer;
